import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, CheckCircle2, MessageSquare, Monitor, Tablet, Smartphone, ExternalLink } from 'lucide-react';
import { trackWhatsAppClick } from '../services/adminStorage';

export function getFormattedDemoUrl(url) {
  if (!url) return '';
  let trimmed = url.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  if (trimmed.startsWith('/models/')) {
    return `models/${trimmed.substring(8)}`;
  }
  if (trimmed.startsWith('/suzume_models/')) {
    return `suzume_models/${trimmed.substring(15)}`;
  }
  return trimmed;
}

export default function ModelPreviewModal({ model, onClose }) {
  const [device, setDevice] = useState('desktop'); // 'desktop' | 'tablet' | 'mobile'

  if (!model) return null;

  const rawDemoUrl = model.demoUrl || model.demo_url || '';
  const demoUrl = getFormattedDemoUrl(rawDemoUrl);

  const handleQuote = () => {
    trackWhatsAppClick(model.id);
    const message = encodeURIComponent(`Olá, Suzume Technologies! Estava visualizando o modelo "${model.name}" e gostaria de solicitar um orçamento para o meu negócio.`);
    window.open(`https://wa.me/5511932654166?text=${message}`, '_blank');
  };

  const getWidthByDevice = () => {
    if (device === 'mobile') return '360px';
    if (device === 'tablet') return '640px';
    return '100%';
  };

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content animate-fade-in" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
        <button className="modal-close-btn" onClick={onClose} aria-label="Fechar">
          <X size={20} />
        </button>

        <div style={{ padding: '1.75rem 2rem 1.25rem', borderBottom: '1px solid var(--border-color)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <span className="badge badge-brand">{model.category}</span>
          </div>
          <h2 style={{ fontSize: '1.75rem', color: 'var(--text-title)', marginBottom: '0.5rem' }}>{model.name}</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem' }}>{model.description}</p>
        </div>

        {/* Toolbar do Simulador */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justify: 'space-between', 
          padding: '0.75rem 2rem', 
          backgroundColor: 'var(--bg-surface-alt)',
          borderBottom: '1px solid var(--border-color)',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)' }}>Visualização:</span>
            <button 
              className={`btn btn-sm ${device === 'desktop' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setDevice('desktop')}
            >
              <Monitor size={15} /> Desktop
            </button>
            <button 
              className={`btn btn-sm ${device === 'tablet' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setDevice('tablet')}
            >
              <Tablet size={15} /> Tablet
            </button>
            <button 
              className={`btn btn-sm ${device === 'mobile' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setDevice('mobile')}
            >
              <Smartphone size={15} /> Mobile
            </button>
          </div>

          <div>
            {demoUrl ? (
              <a 
                href={demoUrl} 
                target="_blank" 
                rel="noreferrer" 
                className="btn btn-secondary btn-sm"
                style={{ textDecoration: 'none' }}
              >
                <ExternalLink size={15} />
                Abrir Demonstração em Nova Aba
              </a>
            ) : (
              <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <ExternalLink size={14} /> Demonstração visual simulada
              </span>
            )}
          </div>
        </div>

        {/* Frame de Simulação */}
        <div style={{ 
          padding: '1.5rem', 
          backgroundColor: '#0f172a', 
          display: 'flex', 
          justify: 'center',
          transition: 'all 0.3s ease'
        }}>
          <div style={{ 
            width: getWidthByDevice(), 
            transition: 'width 0.3s ease',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: 'var(--shadow-lg)',
            border: '1px solid rgba(255,255,255,0.1)',
            background: '#ffffff'
          }}>
            {demoUrl ? (
              <iframe 
                src={demoUrl} 
                title={model.name} 
                style={{ width: '100%', height: '520px', border: 'none', display: 'block' }} 
              />
            ) : (
              <>
                {/* Cabecalho Simulado da Landing Page */}
                <div style={{ 
                  background: model.accentColor || '#1e293b', 
                  color: '#ffffff', 
                  padding: '2.5rem 1.5rem',
                  textAlign: 'center'
                }}>
                  <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.15em', opacity: 0.8 }}>
                    Modelo Exemplo - {model.category}
                  </span>
                  <h3 style={{ fontSize: '1.5rem', color: '#ffffff', marginTop: '0.5rem', marginBottom: '0.75rem' }}>
                    {model.name}
                  </h3>
                  <p style={{ fontSize: '0.9rem', opacity: 0.9, maxWidth: '400px', margin: '0 auto 1.25rem' }}>
                    Layout otimizado para alta conversão de clientes e apresentação impecável dos seus serviços.
                  </p>
                  <button className="btn btn-whatsapp btn-sm" onClick={handleQuote}>
                    <MessageSquare size={16} /> Falar no WhatsApp
                  </button>
                </div>

                {/* Corpo Simulado */}
                <div style={{ padding: '1.5rem', background: '#ffffff' }}>
                  <h4 style={{ fontSize: '1rem', marginBottom: '1rem', color: 'var(--text-title)' }}>
                    Recursos Inclusos neste Modelo:
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: device === 'mobile' ? '1fr' : '1fr 1fr', gap: '0.75rem' }}>
                    {model.features && model.features.map((feat, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.85rem' }}>
                        <CheckCircle2 size={16} color="#10b981" style={{ flexShrink: 0, marginTop: '2px' }} />
                        <span>{feat}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div style={{ 
          padding: '1.25rem 2rem', 
          borderTop: '1px solid var(--border-color)',
          display: 'flex',
          alignItems: 'center',
          justify: 'space-between',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Disponível para assinatura imediata</span>
            <div style={{ fontWeight: 700, color: 'var(--text-title)' }}>Hospedagem + Conta de E-mail + Suporte incluso</div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button className="btn btn-secondary" onClick={onClose}>
              Fechar
            </button>
            <button className="btn btn-whatsapp" onClick={handleQuote}>
              <MessageSquare size={18} />
              Solicitar este Modelo
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
